<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/Page-header";
import committeeQuarterlyReportAPI from "@/services/standards/committee/committeeQuarterlyReportAPI";
import newScoresAPI from "@/services/newScores/newScoresAPI";
import Swal from "sweetalert2";
import VersionNote from "@/components/document/VersionNote.vue";

function currentQuarter() {
  return Math.floor(new Date().getMonth() / 3) + 1;
}

/**
 * 1.1.8 — Editor del informe trimestral del Comité de Convivencia. Selector año/trimestre con
 * precarga desde el seguimiento (reuniones + presidente + actividades sugeridas). Genera PDF branded.
 */
export default {
  page: { title: "Informe trimestral — Comité de Convivencia" },
  components: { VersionNote, Layout, PageHeader },
  data() {
    return {
      // Texto que queda en el control de versiones del PDF
      versionNote: "",
      title: "1.1.8 Informe trimestral del Comité de Convivencia",
      items: [
        { text: "Estándares", href: "/forms/estandares" },
        { text: "Informe trimestral", active: true },
      ],
      companyID: this.$store.getters["company/getCompany"].id || null,
      reportId: this.$route.params.reportId || null,
      years: [],
      trimestres: [
        { v: 1, t: "I — Enero a Marzo" },
        { v: 2, t: "II — Abril a Junio" },
        { v: 3, t: "III — Julio a Septiembre" },
        { v: 4, t: "IV — Octubre a Diciembre" },
      ],
      form: {
        year: new Date().getFullYear(),
        quarter: currentQuarter(),
        fecha: new Date().toISOString().substring(0, 10),
        responsable: "",
        reuniones_ordinarias: 0,
        reuniones_extraordinarias: 0,
        quejas_presentadas: 0,
        quejas_abiertas_anteriores: 0,
        quejas_abiertas_final: 0,
        quejas_cerradas: 0,
        actividades: "",
        recomendaciones: "",
        presidente_nombre: "",
        gerencia_nombre: "",
      },
      submitting: false,
      loading: true,
    };
  },
  watch: {
    "form.year"() { this.autoPrefill(); },
    "form.quarter"() { this.autoPrefill(); },
  },
  methods: {
    rangeYear() {
      for (let i = 2019; i <= 2035; i++) this.years.push(i);
    },
    autoPrefill() {
      if (!this.reportId) this.prefill(false);
    },
    /** Trae datos del módulo de seguimiento. force=true sobrescribe; force=false solo rellena vacíos. */
    prefill(force = false) {
      if (!this.form.year || !this.form.quarter) return;
      committeeQuarterlyReportAPI.suggest(this.companyID, this.form.year, this.form.quarter)
        .then((res) => {
          if (!res) return;
          if (force || (!this.form.reuniones_ordinarias && !this.form.reuniones_extraordinarias)) {
            this.form.reuniones_ordinarias = res.reuniones_total || 0;
          }
          if (res.presidente_nombre && (force || !this.form.presidente_nombre)) {
            this.form.presidente_nombre = res.presidente_nombre;
          }
          if (res.actividades_sugerida && (force || !this.form.actividades)) {
            this.form.actividades = res.actividades_sugerida;
          }
          if (force) {
            Swal.fire("Datos del seguimiento",
              `Se encontraron ${res.reuniones_total || 0} reunión(es) registrada(s) en el trimestre.`, "info");
          }
        })
        .catch((e) => console.log(e));
    },
    load() {
      if (!this.reportId) { this.loading = false; return; }
      committeeQuarterlyReportAPI.show(this.reportId)
        .then((r) => {
          if (r) {
            Object.keys(this.form).forEach((k) => {
              if (r[k] !== undefined && r[k] !== null) this.form[k] = r[k];
            });
            if (r.fecha) this.form.fecha = r.fecha.substring(0, 10);
          }
          this.loading = false;
        })
        .catch(() => { this.loading = false; });
    },
    save() {
      this.submitting = true;
      const payload = { companies_id: this.companyID, ...this.form };
      payload.version_note = this.versionNote;
      const req = this.reportId
        ? committeeQuarterlyReportAPI.update(this.reportId, payload)
        : committeeQuarterlyReportAPI.save(payload);
      req.then((res) => {
        // El informe trimestral es evidencia del ítem 1.1.8 → marca pendiente.
        newScoresAPI.calculateScores({ companies_id: this.companyID, item_id: 8, status: "Pendiente" }).catch(() => {});
        this.submitting = false;
        if (!this.reportId && res && res.id) {
          this.reportId = res.id;
          this.$router.replace({ path: `/forms/estandares/informe-trimestral-convivencia-editar/${res.id}` });
        }
        Swal.fire("¡Guardado!", "El informe trimestral se guardó correctamente.", "success");
      }).catch((e) => {
        this.submitting = false;
        Swal.fire("Error", "No se pudo guardar el informe.", "error");
        console.log(e);
      });
    },
    descargarPdf() {
      if (!this.reportId) {
        Swal.fire("Atención", "Guarda el informe antes de descargar el PDF.", "info");
        return;
      }
      window.open(committeeQuarterlyReportAPI.pdfUrl(this.reportId));
    },
    goBack() {
      this.$router.go(-1);
    },
  },
  mounted() {
    this.rangeYear();
    this.load();
    if (!this.reportId) this.prefill(false);
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row animated animatedFadeInUp fadeInUp">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div v-if="loading" class="text-center py-4"><b-spinner variant="primary" /></div>

            <form v-else @submit.prevent="save">
              <!-- 1. Datos generales -->
              <h5 class="text-primary">1. Datos generales</h5>
              <div class="form-row">
                <b-form-group class="col-md-3" label="Año">
                  <select class="form-control" v-model.number="form.year" required>
                    <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
                  </select>
                </b-form-group>
                <b-form-group class="col-md-3" label="Trimestre">
                  <select class="form-control" v-model.number="form.quarter" required>
                    <option v-for="t in trimestres" :key="t.v" :value="t.v">{{ t.t }}</option>
                  </select>
                </b-form-group>
                <b-form-group class="col-md-3" label="Fecha del informe">
                  <b-form-input type="date" v-model="form.fecha" />
                </b-form-group>
                <b-form-group class="col-md-3" label="Responsable de la elaboración">
                  <b-form-input v-model="form.responsable" maxlength="150" />
                </b-form-group>
              </div>

              <!-- 2. Reuniones -->
              <div class="d-flex align-items-center mt-2">
                <h5 class="text-primary mb-0 mr-3">2. Reuniones realizadas en el trimestre</h5>
                <button type="button" class="btn btn-outline-primary btn-sm" @click="prefill(true)">
                  <i class="mdi mdi-download"></i> Traer del seguimiento
                </button>
              </div>
              <div class="form-row mt-2">
                <b-form-group class="col-md-4" label="Reuniones ordinarias">
                  <b-form-input type="number" min="0" v-model.number="form.reuniones_ordinarias" />
                </b-form-group>
                <b-form-group class="col-md-4" label="Reuniones extraordinarias">
                  <b-form-input type="number" min="0" v-model.number="form.reuniones_extraordinarias" />
                </b-form-group>
                <b-form-group class="col-md-4" label="Total (automático)">
                  <b-form-input :value="form.reuniones_ordinarias + form.reuniones_extraordinarias" disabled />
                </b-form-group>
              </div>

              <!-- 3. Estadísticas de quejas -->
              <h5 class="text-primary mt-2">3. Estadísticas de quejas</h5>
              <div class="form-row">
                <b-form-group class="col-md-3" label="Presentadas en el periodo">
                  <b-form-input type="number" min="0" v-model.number="form.quejas_presentadas" />
                </b-form-group>
                <b-form-group class="col-md-3" label="Abiertas de periodos anteriores">
                  <b-form-input type="number" min="0" v-model.number="form.quejas_abiertas_anteriores" />
                </b-form-group>
                <b-form-group class="col-md-3" label="Abiertas al final del periodo">
                  <b-form-input type="number" min="0" v-model.number="form.quejas_abiertas_final" />
                </b-form-group>
                <b-form-group class="col-md-3" label="Cerradas en el periodo">
                  <b-form-input type="number" min="0" v-model.number="form.quejas_cerradas" />
                </b-form-group>
              </div>

              <!-- 4. Actividades -->
              <h5 class="text-primary mt-2">4. Actividades realizadas</h5>
              <b-form-textarea v-model="form.actividades" rows="4"
                placeholder="Actividades del comité durante el trimestre..." />

              <!-- 5. Recomendaciones -->
              <h5 class="text-primary mt-3">5. Recomendaciones</h5>
              <b-form-textarea v-model="form.recomendaciones" rows="3"
                placeholder="Recomendaciones a la gerencia..." />

              <!-- Firmas (líneas en blanco en el PDF) -->
              <h5 class="text-primary mt-3">Firmas (se firman a mano en el PDF)</h5>
              <div class="form-row">
                <b-form-group class="col-md-6" label="Presidente del Comité de Convivencia">
                  <b-form-input v-model="form.presidente_nombre" maxlength="150" />
                </b-form-group>
                <b-form-group class="col-md-6" label="Representante de la Gerencia (recibe)">
                  <b-form-input v-model="form.gerencia_nombre" maxlength="150" />
                </b-form-group>
              </div>

              <VersionNote v-model="versionNote" :editing="!!reportId" />

              <div class="text-right mt-3">
                <button type="button" class="btn btn-secondary mr-1" @click="goBack">Cancelar</button>
                <button type="button" class="btn btn-outline-primary mr-1" @click="descargarPdf" :disabled="!reportId">
                  <i class="mdi mdi-file-pdf-box"></i> Descargar PDF
                </button>
                <button type="submit" class="btn btn-primary" :disabled="submitting">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
